import { getLocalStorageItemAsString, setLocalStorageItem } from '@atlassian/search-common';

import { getInstance, type ICache } from '../common/cache';

interface RefreshOnAccessArgs {
	/**
	 * Is the identifier for the name in the local storage.
	 */
	name: string;
}

export type CacheAccessState = {
	refreshTriggered: boolean;
	hit: boolean;
};
export type CacheAnalyticsCallback = (state: CacheAccessState) => Promise<void>;

export type NetworkCallbacks = {
	onStart: () => void;
	onSuccess: () => void;
	onError: (errorName: string, errorMessage: string) => void;
};

/**
 * Cache implementation which always returns the persited response (if available).
 * But refreshes the response everytime its requested, so that next time onwards the data is up to date.
 *
 * Note - This will also return an expired result. This strategy should be used only in scenarios where:
 * 1. The data changes rarely.
 * 2. Its fine to serve the expired result to the user.
 */
export const promiseCache = <T>(args: ICache & RefreshOnAccessArgs) => {
	const { name } = args;
	let cacheInstance: ReturnType<typeof getInstance<T>> | null = null;

	const getCacheInstance = () => {
		/**
		 * We lazily create the cache instance to avoid the issue in SSR
		 * where the localStorage isn't available.
		 */
		if (!cacheInstance) {
			cacheInstance = getInstance<T>(args);
			const persistedState = getLocalStorageItemAsString(name);

			if (persistedState) {
				cacheInstance.deserialize(persistedState);
			}
		}
		return cacheInstance;
	};

	const get = (key: string, returnExpired?: boolean) => {
		const storedValue = getCacheInstance().get(key, returnExpired ?? true); // fetch expired result by default
		const isExpired = getCacheInstance().isExpired(key);

		return { storedValue, isExpired };
	};

	const set = async (key: string, result: Promise<T>, expiration?: number) => {
		getCacheInstance().set(key, result, expiration);
		try {
			await result;
			const serialized = await getCacheInstance().serialize();
			if (serialized) {
				setLocalStorageItem(name, serialized);
			}
		} catch (_) {
			// noop - an exception in the result promise or serialisation should be ignored so we dont throw unhandled exceptions
		}
	};

	const clear = (key?: string): void => {
		getCacheInstance().clear(key);
	};

	const refreshOnAccessDecorator = ({
		key,
		supplier,
		returnExpired,
		onlyRefreshIfExpired,
		expiration,
		analyticsCallback,
		networkCallbacks,
	}: {
		key: string;
		supplier: () => Promise<T>;
		returnExpired?: boolean;
		onlyRefreshIfExpired?: boolean;
		expiration?: number;
		analyticsCallback?: CacheAnalyticsCallback;
		networkCallbacks?: NetworkCallbacks;
	}) => {
		const { storedValue, isExpired } = get(key, returnExpired);

		const shouldRefresh = onlyRefreshIfExpired ? isExpired : true;

		if (!storedValue || shouldRefresh) {
			// Wrap the supplier to track network calls
			const trackedSupplier = async () => {
				networkCallbacks?.onStart();
				try {
					const result = await supplier();
					networkCallbacks?.onSuccess();
					return result;
				} catch (error: any) {
					const errorName = error?.name || 'UnknownError';
					const errorMessage = error?.message || 'An unknown error occurred';
					networkCallbacks?.onError(errorName, errorMessage);
					throw error;
				}
			};

			const apiPromise = trackedSupplier();
			set(key, apiPromise, expiration);

			// If there was already a stored value prior to refreshing, return it
			if (storedValue) {
				if (analyticsCallback) {
					analyticsCallback({ refreshTriggered: true, hit: true });
				}
				return storedValue;
			} else {
				if (analyticsCallback) {
					analyticsCallback({ refreshTriggered: true, hit: false });
				}
				return apiPromise;
			}
		} else {
			if (analyticsCallback) {
				analyticsCallback({ refreshTriggered: false, hit: true });
			}
			return storedValue;
		}
	};

	return {
		get,
		refreshOnAccessDecorator,
		clear,
	};
};
